import { useEffect, useMemo, useRef, useState } from "react";
import {
    MagnifyingGlassIcon,
    QrCodeIcon,
    ChevronDownIcon,
    XMarkIcon,
} from "@heroicons/react/24/outline";

import BookCard from "./BookCard";
import RightPanel from "./RightPanel";
import Loader from "../common/Loader";
import { fetchProducts, searchProducts, getProductCategories } from "../../api/productApi";

export default function MainDashboard({ selectedStore, initialCopyId, initialSmartProductId }) {
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");

    /* ---------- STATE ---------- */
    const [allProducts, setAllProducts] = useState([]);
    const [visibleProducts, setVisibleProducts] = useState([]);

    const [bookQuery, setBookQuery] = useState("");
    const [debouncedQuery, setDebouncedQuery] = useState("");

    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [searchLoading, setSearchLoading] = useState(false);

    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [allProductsPage, setAllProductsPage] = useState(1);
    const [allProductsTotalPages, setAllProductsTotalPages] = useState(1);

    const [categories, setCategories] = useState([]);
    const [categoriesLoading, setCategoriesLoading] = useState(false);
    const [categoriesError, setCategoriesError] = useState("");
    const [selectedCategoryId, setSelectedCategoryId] = useState("");
    const [selectedCopyId] = useState(initialCopyId || "");
    const [selectedSmartProductId, setSelectedSmartProductId] = useState(initialSmartProductId || "");

    // Copy Search Modal States
    const [copySearchQuery, setCopySearchQuery] = useState("");
    const [copySearchResults, setCopySearchResults] = useState([]);
    const [copySearchLoading, setCopySearchLoading] = useState(false);
    const [hasCopySearched, setHasCopySearched] = useState(false);

    const [selectedBook, setSelectedBook] = useState(null);
    const [detailsView, setDetailsView] = useState("NONE");
    const [damageInitialData, setDamageInitialData] = useState(null);
    const [isScannerOpen, setIsScannerOpen] = useState(false);

    /* ---------- INFINITE SCROLL REFS ---------- */
    const listContainerRef = useRef(null);

    /* ---------- CACHE KEY ---------- */
    const CACHE_KEY = "sq_cached_products_v1";

    /* ---------- MAPPER ---------- */
    const mapProductToBook = (p) => ({
        id: p.id,
        title: p.name,
        img: p.image || "https://via.placeholder.com/120x180",
        credits: p.price ?? "-",
        stock_status: p.stock_status ?? "Available",
        smart_product_id: p.smart_product_id,
        activeOrder: p.active_order || null,
        copies: Array.isArray(p.copies)
            ? p.copies.map((c) => ({
                id: c.sq_copy_id,
                label: c.copys,
            }))
            : [],
    });

    /* ---------- SELECT BOOK ---------- */
    const selectBook = (book, view = "BOOK_SETTINGS") => {
        setSelectedBook(book);
        setDetailsView(view);
        setDamageInitialData(null);
    };

    /* ---------- DEBOUNCE SEARCH INPUT ---------- */
    useEffect(() => {
        const t = setTimeout(() => {
            setDebouncedQuery(bookQuery.trim());
        }, 600); // 👈 debounce time

        return () => clearTimeout(t);
    }, [bookQuery]);

    /* ---------- INSTANT SEARCH TRIGGER ---------- */
    const triggerSearch = () => {
        const trimmed = bookQuery.trim();
        if (/^\d+$/.test(trimmed)) {
            setSelectedSmartProductId(trimmed);
        } else {
            setSelectedSmartProductId("");
        }
        setDebouncedQuery(trimmed);
    };

    /* ---------- LOAD CACHE FIRST ---------- */
    useEffect(() => {
        const cached = localStorage.getItem(CACHE_KEY);
        if (!cached) return;

        try {
            const parsed = JSON.parse(cached);
            if (Array.isArray(parsed) && parsed.length) {
                setAllProducts(parsed);
                setVisibleProducts(parsed);
            }
        } catch { }
    }, []);

    /* ---------- LOAD CATEGORIES ---------- */
    useEffect(() => {
        if (!token) return;
        let cancelled = false;

        async function loadCategories() {
            setCategoriesLoading(true);
            setCategoriesError("");
            try {
                const res = await getProductCategories({ token });
                if (cancelled) return;
                const rawCategories = Array.isArray(res) ? res : (res?.data || []);
                setCategories(rawCategories);
            } catch (e) {
                console.error("Failed to load categories", e);
                if (!cancelled) {
                    setCategoriesError("Failed to load categories");
                }
            } finally {
                if (!cancelled) setCategoriesLoading(false);
            }
        }

        loadCategories();

        return () => {
            cancelled = true;
        };
    }, [token]);

    /* ---------- INITIAL LOAD ---------- */
    useEffect(() => {
        if (!token) return;

        let cancelled = false;

        async function loadFirstPage() {
            setLoading(true);

            try {
                const res = await fetchProducts({
                    token,
                    page: 1,
                    perPage: 100,
                });

                if (cancelled) return;

                const mapped = (res.data || []).map(mapProductToBook);

                setAllProducts(mapped);
                setVisibleProducts(mapped);

                const currentPage = res.pagination?.current_page || 1;
                const totalP = res.pagination?.total_pages || 1;

                setAllProductsPage(currentPage);
                setAllProductsTotalPages(totalP);
                setPage(currentPage);
                setTotalPages(totalP);

                localStorage.setItem(CACHE_KEY, JSON.stringify(mapped));
            } catch (e) {
                console.error("Failed to load products", e);
            } finally {
                if (!cancelled) setLoading(false);
            }
        }

        loadFirstPage();

        return () => {
            cancelled = true;
        };
    }, [token]);

    /* ---------- LOAD MORE ---------- */
    async function loadMore() {
        if (loadingMore) return;
        if (page >= totalPages) return;

        setLoadingMore(true);

        try {
            const nextPage = page + 1;
            let mapped = [];
            let newPage = nextPage;
            let newTotalPages = totalPages;

            if (debouncedQuery || selectedCategoryId || selectedCopyId || selectedSmartProductId) {
                // We are in search mode
                const res = await searchProducts({
                    query: debouncedQuery,
                    token,
                    page: nextPage,
                    perPage: 100,
                    categoryId: selectedCategoryId || undefined,
                    copyId: selectedCopyId || undefined,
                    smartProductId: selectedSmartProductId || undefined,
                });
                mapped = (res.data || []).map(mapProductToBook);
                newPage = res.pagination?.current_page || nextPage;
                newTotalPages = res.pagination?.total_pages || totalPages;

                const existingIds = new Set(visibleProducts.map((p) => p.id));
                const newItems = mapped.filter((x) => !existingIds.has(x.id));

                setVisibleProducts([...visibleProducts, ...newItems]);
            } else {
                // We are in normal mode
                const res = await fetchProducts({
                    token,
                    page: nextPage,
                    perPage: 100,
                });
                mapped = (res.data || []).map(mapProductToBook);
                newPage = res.pagination?.current_page || nextPage;
                newTotalPages = res.pagination?.total_pages || totalPages;

                const existingIds = new Set(allProducts.map((p) => p.id));
                const newItems = mapped.filter((x) => !existingIds.has(x.id));

                const merged = [...allProducts, ...newItems];

                setAllProducts(merged);
                setVisibleProducts(merged);
                setAllProductsPage(newPage);
                setAllProductsTotalPages(newTotalPages);

                localStorage.setItem(CACHE_KEY, JSON.stringify(merged));
            }

            setPage(newPage);
            setTotalPages(newTotalPages);
        } catch (e) {
            console.error("Failed to load more products", e);
        } finally {
            setLoadingMore(false);
        }
    }

    /* ---------- COPY SEARCH ---------- */
    const handleCopySearch = async () => {
        const query = copySearchQuery.trim();
        if (!query) return;

        setCopySearchLoading(true);
        setHasCopySearched(true);
        try {
            const res = await searchProducts({
                token,
                copy_id: query,
                page: 1,
                perPage: 10,
            });
            const mapped = (res.data || []).map(mapProductToBook);
            setCopySearchResults(mapped);
        } catch (e) {
            console.error("Copy search failed", e);
            setCopySearchResults([]);
        } finally {
            setCopySearchLoading(false);
        }
    };

    /* ---------- INFINITE SCROLL ---------- */
    useEffect(() => {
        const el = listContainerRef.current;
        if (!el) return;

        function onScroll() {
            const threshold = 120;
            const nearBottom =
                el.scrollTop + el.clientHeight >= el.scrollHeight - threshold;

            if (nearBottom) loadMore();
        }

        el.addEventListener("scroll", onScroll);
        return () => el.removeEventListener("scroll", onScroll);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedQuery, selectedCategoryId, selectedCopyId, selectedSmartProductId, page, totalPages, loadingMore, allProducts, visibleProducts]);

    /* ---------- AMAZON SEARCH (DIRECT SHOW FROM API) ---------- */
    useEffect(() => {
        const q = debouncedQuery;
        const catId = selectedCategoryId;
        const cId = selectedCopyId;
        const sId = selectedSmartProductId;

        // If all query parameters are empty, show all loaded products
        if (!q && !catId && !cId && !sId) {
            setSearchLoading(false);
            setVisibleProducts(allProducts);
            setPage(allProductsPage);
            setTotalPages(allProductsTotalPages);
            return;
        }

        // numeric => local ID filter
        if (/^\d+$/.test(q) && !catId && !cId && !sId) {
            setSearchLoading(false);
            const id = Number(q);
            const local = allProducts.filter((p) => Number(p.id) === id);
            setVisibleProducts(local);
            setPage(1);
            setTotalPages(1);
            return;
        }

        let ignore = false;

        async function runSearch() {
            setSearchLoading(true);

            try {
                const res = await searchProducts({
                    query: q,
                    token,
                    page: 1,
                    perPage: 100,
                    categoryId: catId || undefined,
                    copyId: cId || undefined,
                    smartProductId: sId || undefined,
                });
                const mapped = (res.data || []).map(mapProductToBook);

                if (!ignore) {
                    setVisibleProducts(mapped);
                    setPage(res.pagination?.current_page || 1);
                    setTotalPages(res.pagination?.total_pages || 1);
                }
            } catch (e) {
                console.error("Search failed", e);
                if (!ignore) {
                    setVisibleProducts([]);
                    setPage(1);
                    setTotalPages(1);
                }
            } finally {
                if (!ignore) setSearchLoading(false);
            }
        }

        runSearch();

        return () => {
            ignore = true;
        };
    }, [debouncedQuery, selectedCategoryId, selectedCopyId, selectedSmartProductId, token, allProducts, allProductsPage, allProductsTotalPages]);

    const formattedCategories = useMemo(() => {
        // Ignore categories where count = 0
        const activeCategories = categories.filter((c) => Number(c.count) !== 0);

        const activeIds = new Set(activeCategories.map((c) => c.id));
        const categoriesByParent = {};
        activeCategories.forEach((c) => {
            const parentId = c.parent && activeIds.has(c.parent) ? c.parent : 0;
            if (!categoriesByParent[parentId]) {
                categoriesByParent[parentId] = [];
            }
            categoriesByParent[parentId].push(c);
        });

        const ordered = [];
        const traverse = (parentId, depth) => {
            const children = categoriesByParent[parentId] || [];
            children.forEach((child) => {
                ordered.push({
                    ...child,
                    depth,
                });
                traverse(child.id, depth + 1);
            });
        };

        traverse(0, 0);
        return ordered;
    }, [categories]);

    const showBottomHint = useMemo(() => {
        return page < totalPages;
    }, [page, totalPages]);

    /* ---------- UI ---------- */
    return (
        <div className="flex h-screen bg-white min-h-0">

            {/* LEFT PANEL */}
            <div className="w-[28%] min-w-[260px] flex-shrink-0 border-r p-4 min-h-0 flex flex-col gap-4">
                {/* Store */}
                <div className="flex items-center gap-2 text-sm">
                    <span className="font-medium text-gray-600">Store:</span>
                    <span className="text-gray-900 font-semibold bg-gray-100 px-3 py-1.5 rounded-md border">
                        {selectedStore ? selectedStore.name : "No Store Selected"}
                    </span>
                </div>

                {/* Search */}
                <div className="flex items-center gap-3">
                    <button
                        onClick={triggerSearch}
                        className="p-1 rounded hover:bg-gray-100 transition-colors text-gray-400 hover:text-gray-600 focus:outline-none"
                        title="Search"
                    >
                        <MagnifyingGlassIcon className="h-5 w-5" />
                    </button>

                    <div className="flex-1 flex border rounded-md overflow-hidden">
                        <div className="relative border-r">
                            <select
                                value={selectedCategoryId}
                                onChange={(e) => setSelectedCategoryId(e.target.value)}
                                className="appearance-none bg-white py-2 pl-3 pr-8 text-sm outline-none"
                                disabled={categoriesLoading}
                            >
                                {categoriesLoading ? (
                                    <option value="">Loading...</option>
                                ) : categoriesError ? (
                                    <option value="">Error</option>
                                ) : (
                                    <>
                                        <option value="">All Books</option>
                                        {formattedCategories.map((cat) => (
                                            <option key={cat.id} value={cat.id}>
                                                {"\u00A0".repeat(cat.depth * 3) + cat.name}
                                            </option>
                                        ))}
                                    </>
                                )}
                            </select>
                            <ChevronDownIcon className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                        </div>

                        <input
                            value={bookQuery}
                            onChange={(e) => {
                                const val = e.target.value;
                                setBookQuery(val);
                                if (/^\d+$/.test(val.trim())) {
                                    setSelectedSmartProductId(val.trim());
                                } else {
                                    setSelectedSmartProductId("");
                                }
                            }}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    triggerSearch();
                                }
                            }}
                            placeholder="Search Book"
                            className="flex-1 px-3 text-sm outline-none"
                        />

                        <button
                            onClick={() => setIsScannerOpen(true)}
                            className="px-3 border-l text-gray-500 hover:text-black"
                        >
                            <QrCodeIcon className="h-5 w-5" />
                        </button>
                    </div>
                </div>

                {/* MAIN LOADER (only when nothing is visible yet) */}
                {loading && allProducts.length === 0 && (
                    <div className="flex justify-center pt-6">
                        <Loader />
                    </div>
                )}

                {/* SEARCH LOADER */}
                {searchLoading && (
                    <div className="flex justify-center mb-4">
                        <Loader />
                    </div>
                )}

                {/* Empty */}
                {!loading && !searchLoading && visibleProducts.length === 0 && (
                    <div className="text-sm text-gray-400">
                        {debouncedQuery ? "No books found" : "No books available"}
                    </div>
                )}

                {/* Book List */}
                <div
                    ref={listContainerRef}
                    className="flex-1 min-h-0 overflow-y-auto space-y-3 pr-1"
                >
                    {visibleProducts.map((book) => (
                        <BookCard
                            key={book.id}
                            book={book}
                            selectedBook={selectedBook}
                            detailsView={detailsView}
                            onSelect={() => selectBook(book, "BOOK_SETTINGS")}
                            onIconClick={(b, v) => selectBook(b, v)}
                        />
                    ))}

                    {/* Infinite scroll loader at bottom */}
                    {loadingMore && (
                        <div className="flex justify-center py-6">
                            <Loader />
                        </div>
                    )}

                    {/* hint */}
                    {showBottomHint && !loadingMore && (
                        <div className="py-2 text-center text-sm text-gray-300">
                            Scroll to load more
                        </div>
                    )}

                    {/* End */}
                    {page >= totalPages && visibleProducts.length > 0 && (
                        <div className="py-2 text-center text-sm text-gray-200">
                            End of list
                        </div>
                    )}
                </div>
            </div>

            {/* RIGHT PANEL */}
            <div className="flex-1 p-6 overflow-y-auto bg-white">
                <RightPanel
                    view={detailsView}
                    book={selectedBook}
                    damageInitialData={damageInitialData}
                    onViewChange={(view, initialData = null) => {
                        setDetailsView(view);
                        if (initialData) {
                            setDamageInitialData(initialData);
                        }
                    }}
                    onClose={() => {
                        setSelectedBook(null);
                        setDetailsView("NONE");
                        setDamageInitialData(null);
                    }}
                />
            </div>

            {/* SCANNER / COPY SEARCH MODAL */}
            {isScannerOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl p-6 relative max-w-lg w-full text-left shadow-2xl border border-gray-100 flex flex-col gap-4">
                        {/* Header */}
                        <div className="flex items-center justify-between pb-3 border-b border-gray-100">
                            <div>
                                <h3 className="text-base font-bold text-gray-900">Copy Search</h3>
                                <p className="text-xs text-gray-500 mt-0.5">Search products by Copy ID</p>
                            </div>
                            <button
                                onClick={() => {
                                    setIsScannerOpen(false);
                                    setCopySearchQuery("");
                                    setCopySearchResults([]);
                                    setCopySearchLoading(false);
                                    setHasCopySearched(false);
                                }}
                                className="p-1 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                            >
                                <XMarkIcon className="h-5 w-5" />
                            </button>
                        </div>

                        {/* Input Row */}
                        <div className="flex items-center gap-3">
                            <input
                                type="text"
                                value={copySearchQuery}
                                onChange={(e) => setCopySearchQuery(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") handleCopySearch();
                                }}
                                placeholder="Enter Copy ID (e.g. 101244901)"
                                className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                autoFocus
                            />
                            <button
                                onClick={handleCopySearch}
                                className="bg-black hover:bg-black active:scale-95 text-white rounded-lg px-4 py-2 text-sm font-bold transition-all flex items-center gap-1.5"
                                disabled={copySearchLoading}
                            >
                                {copySearchLoading ? "Searching..." : "Search"}
                            </button>
                        </div>

                        {/* Results Section */}
                        <div className="min-h-[140px] flex flex-col justify-center border border-dashed border-gray-200 rounded-xl p-4 bg-gray-50/50">
                            {copySearchLoading ? (
                                <div className="flex justify-center items-center py-8">
                                    <Loader />
                                </div>
                            ) : copySearchResults.length > 0 ? (
                                <div className="space-y-3">
                                    <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">Matching Product</p>
                                    {copySearchResults.map((book) => (
                                        <BookCard
                                            key={book.id}
                                            book={book}
                                            selectedBook={selectedBook}
                                            detailsView={detailsView}
                                            onSelect={() => {
                                                selectBook(book, "BOOK_SETTINGS");
                                                setIsScannerOpen(false);
                                                setCopySearchQuery("");
                                                setCopySearchResults([]);
                                                setCopySearchLoading(false);
                                                setHasCopySearched(false);
                                            }}
                                            onIconClick={(b, v) => {
                                                selectBook(b, v);
                                                setIsScannerOpen(false);
                                                setCopySearchQuery("");
                                                setCopySearchResults([]);
                                                setCopySearchLoading(false);
                                                setHasCopySearched(false);
                                            }}
                                        />
                                    ))}
                                </div>
                            ) : hasCopySearched ? (
                                <div className="text-center py-8 text-gray-400 text-sm">
                                    No product found
                                </div>
                            ) : (
                                <div className="text-center py-8 text-gray-400 text-sm">
                                    Enter a Copy ID above and click Search
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div >
    );
}
